import React from "react";
import { StickerDef, fitLines } from "./StickerDesigns";

// The viral DEFAULT sticker — a clean "trick title" badge auto-built from the
// clip's own data (trick name + sport + spot). No score, no rank: it names the
// trick and where it went down, branded, and lets the footage be the flex.
// Renders into the standard 512×512 sticker viewBox so it flows through the same
// placed-sticker + composite pipeline as every other sticker (fully movable,
// scalable, and DELETABLE — it is auto-placed, never forced).

const VB = 512;
const ELECTRIC_BLUE = "#0affff";
const WHITE = "#FFFFFF";
const MUTED = "#AEB6C2";
const BLACK = "#000000";

export interface TrickStatInput {
    trickName?: string;
    sport?: string;
    location?: string;
}

export const TRICK_STAT_STICKER_ID = "trickstat:default";

// Pull a trailing spin/degree number ("720", "1080", "180") onto its own line so
// it reads as the hero. "SWITCH CORK 720" → { top: "SWITCH CORK", num: "720" }.
export function splitTrickName(name: string): { top: string; num: string } {
    const t = (name || "").trim().toUpperCase();
    const m = t.match(/^(.*?)\s*(\d{2,4})$/);
    if (m && m[1].trim()) return { top: m[1].trim(), num: m[2] };
    return { top: t, num: "" };
}

// The "SPORT · SPOT" subline, omitting whichever piece is missing.
export function trickStatSubline(sport?: string, location?: string): string {
    return [sport, location]
        .map((s) => (s || "").trim())
        .filter(Boolean)
        .join(" · ")
        .toUpperCase();
}

const ANTON = "Anton, 'Mukta Mahee', sans-serif";
const MUKTA = "'Mukta Mahee', system-ui, sans-serif";
const NEON_PINK = "#ff6ad5";
const CARD_W = 446;

// Fit a single line to a target width (condensed Anton glyphs ~0.52em advance).
function fitText(text: string, maxW: number, cap: number): number {
    const len = Math.max(text.trim().length, 1);
    return Math.max(22, Math.min(cap, Math.floor(maxW / (0.52 * len))));
}

// The default "first" sticker — a branded Ecliptic trick card. Signature moves:
// a neon ECLIPTIC chip header (the lockup), the spin degree as a giant glowing
// hero numeral ringed by a spin arc, the trick name in condensed display caps,
// the spot in a muted strap, and a single rare neon-pink corner accent. Adapts
// when there's no spin number (the name becomes the hero) and wraps long names.
export function buildTrickStatSticker({ trickName, location }: TrickStatInput): StickerDef {
    const { top, num } = splitTrickName((trickName || "").trim() || "JUST SENT IT");
    const spot = (location || "").trim().toUpperCase();

    // Trick name prefers a single title line (it sits above the hero number);
    // it only wraps when one line would be too small to read.
    const nameCap = num ? 82 : 104;
    const oneLineSize = fitText(top, CARD_W - 92, nameCap);
    const { lines: nameLines, size: nameSize } =
        !top.includes(" ") || oneLineSize >= 42
            ? { lines: [top], size: oneLineSize }
            : fitLines(top.toUpperCase(), num ? 2 : 3, CARD_W - 92, num ? 190 : 280, nameCap);
    const nameLineH = nameSize * 0.98;
    const nameBlockH = nameLines.length * nameLineH;
    const degSize = num ? fitText(num, CARD_W - 150, 180) : 0;
    const degH = degSize * 0.84;
    const chipH = 50;
    const spotH = spot ? 30 : 0;

    const G1 = 22; // chip → name
    const G2 = 12; // name → degree
    const G3 = 22; // → spot
    const blockH = chipH + G1 + nameBlockH + (num ? G2 + degH : 0) + (spot ? G3 + spotH : 0);
    const PAD = 40;
    const cardH = blockH + PAD * 2;
    const cardY = (VB - cardH) / 2;
    const cardX = (VB - CARD_W) / 2;
    const cx = VB / 2;

    let y = cardY + PAD;
    const chipCY = y + chipH / 2;
    y += chipH + G1;
    const nameTop = y;
    y += nameBlockH;
    let degCY = 0;
    if (num) { y += G2; degCY = y + degH / 2; y += degH; }
    let spotBase = 0;
    if (spot) { y += G3; spotBase = y + spotH * 0.78; }

    const chipW = 190;
    const chipX = cx - chipW / 2;
    const arcR = degH * 0.66;

    return {
        id: TRICK_STAT_STICKER_ID,
        family: "Generic",
        variant: "hero" as never,
        render: () => (
            <g>
                <defs>
                    <linearGradient id="ts-card" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0c0e12" />
                        <stop offset="100%" stopColor="#050507" />
                    </linearGradient>
                </defs>

                {/* card — floodlit edge */}
                <g style={{ filter: "drop-shadow(0 0 28px rgba(10,255,255,0.42))" }}>
                    <rect x={cardX} y={cardY} width={CARD_W} height={cardH} rx={36}
                        fill="url(#ts-card)" stroke={ELECTRIC_BLUE} strokeWidth={4.5} />
                    <rect x={cardX + 11} y={cardY + 11} width={CARD_W - 22} height={cardH - 22} rx={27}
                        fill="none" stroke={ELECTRIC_BLUE} strokeWidth={1.5} opacity={0.26} />
                </g>

                {/* rare second accent — neon-pink corner tick */}
                <path d={`M ${cardX + CARD_W - 66} ${cardY + 18} L ${cardX + CARD_W - 18} ${cardY + 18} L ${cardX + CARD_W - 18} ${cardY + 66}`}
                    fill="none" stroke={NEON_PINK} strokeWidth={5} strokeLinecap="round"
                    style={{ filter: `drop-shadow(0 0 8px ${NEON_PINK})` }} />

                {/* spin arc ringing the degree */}
                {num && (
                    <g opacity={0.5} style={{ filter: "drop-shadow(0 0 10px rgba(10,255,255,0.9))" }}>
                        <circle cx={cx} cy={degCY} r={arcR} fill="none" stroke={ELECTRIC_BLUE} strokeWidth={3}
                            strokeDasharray={`${arcR * 4.2} ${arcR * 2.2}`} strokeLinecap="round" />
                        <circle cx={cx} cy={degCY} r={arcR + 15} fill="none" stroke={ELECTRIC_BLUE} strokeWidth={1.5}
                            strokeDasharray="3 17" opacity={0.55} />
                    </g>
                )}

                {/* ECLIPTIC chip (brand lockup) */}
                <g style={{ filter: "drop-shadow(0 0 12px rgba(10,255,255,0.55))" }}>
                    <rect x={chipX} y={chipCY - chipH / 2} width={chipW} height={chipH} rx={chipH / 2} fill={ELECTRIC_BLUE} />
                    <circle cx={chipX + 28} cy={chipCY} r={7} fill={NEON_PINK} />
                    <text x={cx + 14} y={chipCY + 8} textAnchor="middle" fill={BLACK}
                        fontFamily={MUKTA} fontWeight={700} fontSize={23} letterSpacing={5}>
                        ECLIPTIC
                    </text>
                </g>

                {/* trick name */}
                {nameLines.map((ln, i) => (
                    <text key={i} x={cx} y={nameTop + i * nameLineH + nameSize * 0.8} textAnchor="middle"
                        fill={WHITE} fontFamily={ANTON} fontSize={nameSize} letterSpacing={1}
                        stroke={BLACK} strokeWidth={2} paintOrder="stroke fill">
                        {ln}
                    </text>
                ))}

                {/* degree hero */}
                {num && (
                    <text x={cx} y={degCY + degSize * 0.34} textAnchor="middle" fill={ELECTRIC_BLUE}
                        fontFamily={ANTON} fontSize={degSize} letterSpacing={-1}
                        stroke={BLACK} strokeWidth={5} paintOrder="stroke fill"
                        style={{ filter: "drop-shadow(0 0 20px rgba(10,255,255,0.65))" }}>
                        {num}
                    </text>
                )}

                {/* spot strap */}
                {spot && (
                    <text x={cx} y={spotBase} textAnchor="middle" fill={MUTED}
                        fontFamily={MUKTA} fontWeight={700} fontSize={22} letterSpacing={3}>
                        {spot}
                    </text>
                )}
            </g>
        ),
    };
}
